import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Grid, TextField, Box, Button, Paper,
  Stack, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  InputLabel, Select, MenuItem, Checkbox, FormHelperText, Typography,
  InputAdornment, IconButton
} from '@mui/material';
import { PersonAddAlt1, ArrowForwardIos } from '@mui/icons-material';
import * as initialList from '../../services/initialsList';
import * as maritalStatus from '../../services/maritalStatus';
import * as esicExempted from '../../services/esicExempted';
import * as leavingReason from '../../services/leavingReason';
import ViewEmpSalComponents from './ViewEmpSalComponents';
import Popup from '../Popup';
import '../../App.css';
import Notification from '../Notification';
import { useDispatch, useSelector } from 'react-redux';
import { updateTableActions } from '../../store/update-table-slice';
import { useNavigate } from 'react-router-dom';

const initialsOptions = initialList.getInitialsList();
const maritalStatusOptions = maritalStatus.getMaritalStatus();
const esicExemptedOptions = esicExempted.getEsicExempted();
const leavingReasonOptions = leavingReason.getLeavingReasons();

let employee_id = 0;

const initialFValues = {
  id: 0,
  company: '',
  initial: '',
  roleId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  full_name: '',
  email: '',
  birthDate: new Date().toISOString().split('T')[0],
  gender: 'none',
  maritalStatus: '',
  parentsName: '',
  relation: '',
  address: '',
  pinCode: '',
  stdCode: '',
  stdTele: '',
  esicNumber: '',
  esicExempted: '1',
  mobile: '',
  employeeCode: '',
  dateOfJoining: new Date().toISOString().split('T')[0],
  originalDateOfJoining: '',
  originalDateOfTermination: '',
  salAccNo: '',
  bank_name: '',
  ifsc_code: '',
  pan: '',
  isPfApplicable: false,
  isVpfApplicable: false,
  isWfApplicable: false,
  isLeaving: false,
  pfNo: '',
  pfLimit: 0.0,
  vpfDeductionType: 'P',
  vpfPercent: 0.0,
  vpfFixedAmount: 0.0,
  uanNo: '',
  wfNumber: '',
  dateOfResignation: null,
  dateOfLeaving: null,
  officialNoticePeriod: '',
  noticeGivenByWhom: '',
  considerNoticePeriod: false,
  shortNoticePeriod: false,
  earningOrRecovery: false,
  isNoticePayout: false,
  isRegrettedExit: false,
  leavingReason: '',
  carryOverLeave: 0.0,
  carryOverTillDate: new Date().toISOString().split('T')[0]
};

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function AddEmployeeForm(props) {
  const hideStatutory = !!props.hideStatutory;
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [employee, setEmployee] = useState({});
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const dispatch = useDispatch();
  const updateEmployees = useSelector(state => state.updateEmployees);
  const [WF_Applicable_for_Company, setWF_Applicable_for_Company] = useState(0);
  const navigate = useNavigate();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  useEffect(() => {
    // Only close modal when employee is successfully saved (not for code generation success)
    if (notify.isOpen && notify.type === 'success' && notify.message === 'Successfully added') {
      const timer = setTimeout(() => {
        if (props.onCloseModal) {
          props.onCloseModal();
        }
        setOpenPopup(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [notify, props.onCloseModal]);

  // Auto-generate employee code when adding new employee
  useEffect(() => {
    if (!hideStatutory && props.fetch_id === 9999 && !values.employeeCode) {
      handleAutoGenerateEmployeeCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fetch_id]);

  // Auto-generate full_name (ECR repo) ONLY for new employee (fetch_id === 9999)
  // For existing employees we trust and keep whatever backend sends for full_name.
  useEffect(() => {
    if (props.fetch_id !== 9999) return;

    const ecrName = `${values.firstName || ''} ${values.middleName || ''} ${values.lastName || ''}`
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

    setValues(prevValues => {
      if (prevValues.full_name !== ecrName) {
        return {
          ...prevValues,
          full_name: ecrName
        };
      }
      return prevValues;
    });
  }, [props.fetch_id, values.firstName, values.middleName, values.lastName]);

  const validate = () => {
    let temp = {};
    temp.firstName = values.firstName ? "" : "This field is required.";
    temp.lastName = values.lastName ? "" : "This field is required.";
    temp.address = values.address ? "" : "This field is required.";
    temp.pinCode = values.pinCode ? (/^\d{6}$/.test(values.pinCode) ? "" : "Pin Code must be a 6-digit numeric value.") : "This field is required.";
    temp.salAccNo = values.salAccNo
      ? (isNaN(parseFloat(values.salAccNo))
          ? `${hideStatutory ? 'Stipend' : 'Salary'} Account Number must be a numeric value.`
          : "")
      : "This field is required.";
    temp.pan = values.pan ? (/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(values.pan.toUpperCase()) ? "" : "Please enter a valid PAN: 5 letters, 4 numbers, and 1 letter (e.g., ABCDE1234F).") : "This field is required.";
    temp.email = values.email ? (/^$|.+@.+\..+/).test(values.email) ? "" : "Email is not valid." : "This field is required.";
    temp.mobile = values.mobile.length === 10 ? "" : "Mobile number must be 10 digits.";
    temp.initial = values.initial.length !== 0 ? "" : "This field is required.";
    temp.roleId = values.roleId ? "" : "This field is required.";
    temp.employeeCode = values.employeeCode ? "" : "This field is required.";
    temp.maritalStatus = values.maritalStatus.length !== 0 ? "" : "This field is required.";
    temp.birthDate = values.birthDate ? (new Date().getFullYear() - new Date(values.birthDate).getFullYear() >= 18 ? "" : "Age must be greater than 18") : "This field is required.";
    if (!values.dateOfJoining) {
      temp.dateOfJoining = "This field is required.";
    } else {
      const joiningDate = new Date(values.dateOfJoining);
      const birthDate = new Date(values.birthDate);
      if (isNaN(joiningDate.getTime()) || isNaN(birthDate.getTime())) {
        temp.dateOfJoining = "Please enter a valid date.";
      } else {
        const ageAtJoining = joiningDate.getFullYear() - birthDate.getFullYear();
        const monthDiff = joiningDate.getMonth() - birthDate.getMonth();
        const dayDiff = joiningDate.getDate() - birthDate.getDate();
        const actualAge = ageAtJoining - (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? 1 : 0);
        temp.dateOfJoining = actualAge >= 18 ? "" : "Employee must be at least 18 years old at the time of joining.";
      }
    }
    temp.gender = values.gender !== 'none' ? "" : "Please select a gender.";

    if (!hideStatutory) {
      temp.esicExempted = values.esicExempted?.length !== 0 ? "" : "This field is required.";

      // Conditionally validate the esicNumber
      if (values.esicExempted === '0') {
        temp.esicNumber = values.esicNumber ? "" : "ESIC Number is required.";
      }

      // Validate PF related fields when PF is applicable
      if (values.isPfApplicable) {
        temp.pfNo = values.pfNo ? "" : "PF Number is required.";
        temp.pfLimit = values.pfLimit ? "" : "PF Limit is required.";
        temp.uanNo = values.uanNo ? (/^\d{12}$/.test(values.uanNo) ? "" : "UAN Number must be a 12-digit numeric value.") : "UAN Number is required.";

        if (values.isVpfApplicable) {
          if (values.vpfDeductionType === 'A') {
            const amt = parseFloat(values.vpfFixedAmount);
            temp.vpfFixedAmount = (!isNaN(amt) && amt > 0) ? "" : "VPF Amount must be greater than 0.";
          } else {
            const pct = parseFloat(values.vpfPercent);
            temp.vpfPercent = (!isNaN(pct) && pct > 0) ? "" : "VPF % must be greater than 0.";
          }
        }
      }

      // Validate WF related fields when WF is applicable
      if (values.isWfApplicable) {
        temp.wfNumber = values.wfNumber ? "" : "WF Number is required.";
      }
    }

    // IFSC validation: optional but must match standard format when provided
    if (values.ifsc_code) {
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      temp.ifsc_code = ifscRegex.test(values.ifsc_code) ? "" : "IFSC must be 11 chars: 4 letters, '0', then 6 alphanumerics (e.g. ABCD0EFG123)";
    }

    setErrors({
      ...temp
    });

    return Object.values(temp).every(x => x === "");
  };

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
  };

  const convertToDefEventPara = (name, value) => ({
    target: {
      name: name,
      value: value
    }
  });

  function handleInputChange(e) {
    const { name, value } = e.target;
    let newValue = value;
   
    // Define text-only fields (only letters and spaces allowed)
    const textOnlyFields = ['firstName', 'middleName', 'lastName', 'parentsName', 'address', 'bank_name', 'full_name'];
    
    // Define integer-only fields (only digits, no decimals)
    const integerOnlyFields = ['pinCode', 'mobile', 'salAccNo', 'esicNumber', 'uanNo', 'stdCode', 'stdTele', 'carryOverLeave', 'wfNumber'];
    
    // Define decimal fields (digits with optional decimal point)
    const decimalFields = ['pfLimit', 'vpfPercent', 'vpfFixedAmount'];
    
    // Apply input restrictions
    if (textOnlyFields.includes(name)) {
      // Only allow letters, spaces, apostrophes, and hyphens
      newValue = value.replace(/[^a-zA-Z\s'-]/g, '');
    } else if (name === 'employeeCode' && hideStatutory) {
      // Intern/Apprentice code: allow alphanumeric + - _ /
      newValue = value.toUpperCase().replace(/[^A-Z0-9/_-]/g, '');
    } else if (integerOnlyFields.includes(name)) {
      // Only allow digits (no decimals)
      newValue = value.replace(/[^\d]/g, '');
    } else if (decimalFields.includes(name)) {
      // Allow digits and decimal point
      newValue = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = newValue.split('.');
      if (parts.length > 2) {
        newValue = parts[0] + '.' + parts.slice(1).join('');
      }
    } else if (name === 'pan') {
      // PAN: Allow only uppercase letters and numbers
      newValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    } else if (name === 'ifsc_code') {
      // Normalize IFSC to uppercase alphanumerics and limit length to 11
      newValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    } else if (name === 'full_name') {
      // ECR name: capital casing (uppercase) for name as per ECR field
      newValue = value.toUpperCase().replace(/[^A-Z\s'-]/g, '');
    } else if (name === "esicExempted") {
      newValue = value.toString();
    }

    // Is Recovery and Is Payout are mutually exclusive;
    // Notice by Company → payout only; Notice by Employee → recovery only
    if (name === 'noticeGivenByWhom') {
      setValues({
        ...values,
        noticeGivenByWhom: newValue,
        earningOrRecovery: newValue === '2' ? values.earningOrRecovery : false,
        isNoticePayout: newValue === '1' ? values.isNoticePayout : false
      });
    } else if (name === 'earningOrRecovery' && newValue === true) {
      setValues({
        ...values,
        earningOrRecovery: true,
        isNoticePayout: false
      });
    } else if (name === 'isNoticePayout' && newValue === true) {
      setValues({
        ...values,
        isNoticePayout: true,
        earningOrRecovery: false
      });
    } else {
      setValues({
        ...values,
        [name]: newValue
      });
    }
   
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  }

  useEffect(() => {
    async function getEmployee() {
      if (props.fetch_id !== 9999) {
        try {
          const employees = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/get-employee/` + props.fetch_id);
          const emp = employees.data[0];
          setEmployee(emp);

          const editFValues = {
            initial: emp.initial,
            roleId: emp.roleId || emp.role_id || '',
            firstName: emp.first_name,
            middleName: emp.middle_name,
            lastName: emp.last_name,
            full_name: emp.full_name || `${emp.first_name || ''} ${emp.middle_name || ''} ${emp.last_name || ''}`.trim().toUpperCase(),
            email: emp.email,
            birthDate: emp.birth_date.split('T')[0],
            gender: emp.gender,
            maritalStatus: emp.marital_status,
            parentsName: emp.Father_Husband_Name,
            relation: emp.Relationship_with_member,
            address: emp.address,
            pinCode: emp.pin_code,
            stdTele: emp.telephone,
            esicNumber: emp.ESINumber,
            esicExempted: emp.EsicExempted,
            mobile: emp.mobile_no,
            employeeCode: emp.employee_code,
            dateOfJoining: emp.date_of_joining.split('T')[0],
            originalDateOfJoining: emp.original_date_of_joining
              ? String(emp.original_date_of_joining).split('T')[0]
              : '',
            originalDateOfTermination: emp.original_date_of_termination
              ? String(emp.original_date_of_termination).split('T')[0]
              : '',
            salAccNo: emp.ICICI_account_no,
            bank_name: emp.bank_name || '',
            pan: emp.pan_card_no,
            ifsc_code: emp.ifsc_code || '',
            isPfApplicable: emp.pf_applicable,
            isVpfApplicable: Number(emp.vpf_applicable) === 1,
            isWfApplicable: emp.welfare_fund_applicable,
            isLeaving: emp.date_of_termination !== null,
            pfNo: emp.pf_number,
            pfLimit: emp.PFLimitRs,
            vpfDeductionType: emp.vpf_deduction_type === 'A' ? 'A' : 'P',
            vpfPercent: emp.VPFPercentage ?? 0.0,
            vpfFixedAmount: emp.VPFFixedAmount ?? 0.0,
            uanNo: emp.UANNumber,
            wfNumber: emp.wf_number || '',
            dateOfResignation: emp.date_of_resignation == null ? null : (typeof emp.date_of_resignation === 'string' ? emp.date_of_resignation.split('T')[0] : emp.date_of_resignation),
            dateOfLeaving: emp.date_of_termination == null ? null : (typeof emp.date_of_termination === 'string' ? emp.date_of_termination.split('T')[0] : emp.date_of_termination),
            officialNoticePeriod: emp.official_notice_period != null ? String(emp.official_notice_period) : '',
            noticeGivenByWhom: emp.notice_given_by_whom != null && emp.notice_given_by_whom !== '' ? String(emp.notice_given_by_whom) : '',
            considerNoticePeriod: Number(emp.consider_notice_period) === 1,
            shortNoticePeriod: Number(emp.short_notice_period) === 1,
            earningOrRecovery: Number(emp.earning_or_recovery) === 1,
            isNoticePayout: Number(emp.is_notice_payout) === 1,
            leavingReason: emp.Reason_For_Leaving ?? '',
            isRegrettedExit: emp.IsRegrettedExit === true || emp.IsRegrettedExit === 1,
            carryOverLeave: emp.leave_carryover,
            carryOverTillDate: emp.carryover_date === null ? null : emp.carryover_date.split('T')[0],
          };

          setValues(editFValues);
        } catch (e) {
          if (e.response.status === 401) {
            setNotify({
              isOpen: true,
              message: 'Session Expired!',
              type: 'error'
            });
            setTimeout(() => {
              navigate('/');
            }, 1000);
          }
        }
      }
    }
    getEmployee();

    async function getAllLeaveTypes() {
      try {
        const leaveTypes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/company-master/leave-master/view`);
        setLeaveTypes(leaveTypes.data.map((leave) => leave.leave_type));
      } catch (e) {
        if (e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      }
    }
    getAllLeaveTypes();

    async function getAllRoles() {
      try {
        const rolesUrl = hideStatutory
          ? `${process.env.REACT_APP_API_BASE_URL}/apprentice-master/intern-apprentice-options`
          : `${process.env.REACT_APP_API_BASE_URL}/employee-master/get-employee-roles`;

        const response = await axios.get(rolesUrl);
        if (response.status === 200 && Array.isArray(response.data)) {
          const normalized = hideStatutory
            ? response.data.map(r => ({
                role_id: r.id,
                role_name: r.name
              }))
            : response.data;
          setRoles(normalized);
        }
      } catch (e) {
        if (e.response && e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      }
    }
    getAllRoles();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/configure/wf-isapplicable`, { credentials: 'include' });
        const responseData = await response.json();
        setWF_Applicable_for_Company(responseData[0].WF_Applicable);
        if (response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 500);
        }
      } catch (e) {
        // Handle error
      }
    })();
  }, []);

  const handleAutoGenerateEmployeeCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/get-auto-emp-code`);
      if (response.status === 200 && response.data) {
        // Handle different possible response formats
        let employeeCode = '';
        if (typeof response.data === 'string') {
          employeeCode = response.data;
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          const firstItem = response.data[0];
          employeeCode = firstItem.employeeCode || firstItem.employee_code || firstItem || '';
        } else if (typeof response.data === 'object') {
          employeeCode = response.data.employeeCode || response.data.employee_code || response.data.code || '';
        }
        
        if (employeeCode) {
          setValues({
            ...values,
            employeeCode: String(employeeCode)
          });
          setNotify({
            isOpen: true,
            message: 'Employee code generated successfully',
            type: 'success'
          });
        }
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error'
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        } else {
          setNotify({
            isOpen: true,
            message: 'Failed to generate employee code',
            type: 'error'
          });
        }
      } else {
        setNotify({
          isOpen: true,
          message: 'Network error. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      let API_URL = '';
      if (props.fetch_id === 9999) {
        API_URL = `${process.env.REACT_APP_API_BASE_URL}/employee-master/add-employee`;
      } else {
        API_URL = `${process.env.REACT_APP_API_BASE_URL}/employee-master/edit-employee/` + props.fetch_id;
      }
      const payload = {
        ...values,
        ...(hideStatutory
          ? {
              esicNumber: '',
              esicExempted: '1',
              isPfApplicable: false,
              isVpfApplicable: false,
              isWfApplicable: false,
              pfNo: '',
              pfLimit: 0.0,
              vpfDeductionType: 'P',
              vpfPercent: 0.0,
              vpfFixedAmount: 0.0,
              uanNo: '',
              wfNumber: '',
            }
          : {
              isVpfApplicable: values.isPfApplicable ? values.isVpfApplicable : false,
              vpfDeductionType: values.vpfDeductionType === 'A' ? 'A' : 'P',
              vpfPercent: values.isVpfApplicable && values.vpfDeductionType !== 'A' ? values.vpfPercent : 0.0,
              vpfFixedAmount: values.isVpfApplicable && values.vpfDeductionType === 'A' ? values.vpfFixedAmount : 0.0,
            }),
        dateOfResignation: values.dateOfResignation ?? null,
        dateOfLeaving: values.dateOfLeaving ?? null,
        noticeGivenByWhom: values.noticeGivenByWhom === '' || values.noticeGivenByWhom == null ? '' : (values.noticeGivenByWhom === '1' ? 1 : 2),
        considerNoticePeriod: values.considerNoticePeriod ? 1 : 0,
        shortNoticePeriod: values.shortNoticePeriod ? 1 : 0,
        // Company → payout only; Employee → recovery only
        earningOrRecovery: values.noticeGivenByWhom === '2' && values.earningOrRecovery ? 1 : 0,
        isNoticePayout: values.noticeGivenByWhom === '1' && values.isNoticePayout ? 1 : 0
      };
      // ECR name: ensure full_name is in capital casing
      if (payload.full_name) {
        payload.full_name = String(payload.full_name).trim().toUpperCase();
      }
      axios.post(API_URL, payload)
        .then(function (response) {
          if (response.status === 200) {
            employee_id = response.data.lastInsertId;

            // When editing, patch full_name from API response back into the form
            if (props.fetch_id !== 9999 && response.data && response.data.full_name) {
              const apiFullName = String(response.data.full_name).trim().toUpperCase();
              setValues(prev => ({
                ...prev,
                full_name: apiFullName
              }));
            }

            setNotify({
              isOpen: true,
              message: 'Successfully added',
              type: 'success'
            });
            dispatch(updateTableActions.startUpdateEmployees());
            setButtonDisabled(true);
          }
        })
        .catch(function (error) {
          if (error.response) {
            if (error.response.status === 401) {
              setNotify({
                isOpen: true,
                message: 'Session Expired!',
                type: 'error'
              });
              setTimeout(() => {
                navigate('/');
              }, 1000);
            } else if (error.response.status === 400) {
              // Handle duplicate mobile/PAN errors
              const errorMessage = error.response.data.error_message;
              
              if (errorMessage.includes('Mobile number already exists')) {
                setNotify({
                  isOpen: true,
                  message: 'Employee with this mobile number already exists!',
                  type: 'error'
                });
                setErrors({
                  ...errors,
                  mobile: "Mobile number already exists"
                });
              } else if (errorMessage.includes('PAN number already exists')) {
                setNotify({
                  isOpen: true,
                  message: 'Employee with this PAN number already exists!',
                  type: 'error'
                });
                setErrors({
                  ...errors,
                  pan: "PAN number already exists"
                });
              } else {
                setNotify({
                  isOpen: true,
                  message: errorMessage,
                  type: 'error'
                });
              }
            } else {
              setNotify({
                isOpen: true,
                message: 'An error occurred while saving employee',
                type: 'error'
              });
            }
          } else {
            setNotify({
              isOpen: true,
              message: 'Network error. Please try again.',
              type: 'error'
            });
          }
        });
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", margin: "10px", padding: "30px", fontWeight: "500px", marginTop: "-21px" }}>
      <Paper sx={{ width: "100%", maxWidth: "800px" }}>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ margin: "30px" }}>
            <FormControl
              variant="outlined"
              {...(errors.initial && { error: true })}
              fullWidth
            >
              <InputLabel><RequiredLabel label="Initial"></RequiredLabel></InputLabel>
              <Select
                label={<RequiredLabel label="Initial" />}
                name="initial"
                size="small"
                value={values.initial}
                onChange={handleInputChange}
              >
                <MenuItem value="">None</MenuItem>
                {
                  initialsOptions.map(
                    item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>)
                  )
                }
              </Select>
              {errors.initial && <FormHelperText>{errors.initial}</FormHelperText>}
            </FormControl>

            <Stack direction="row" spacing={2}>
              <TextField
                variant="outlined"
                name="firstName"
                size="small"
                label={<RequiredLabel label="First Name" />}
                value={values.firstName}
                onChange={handleInputChange}
                {...(errors.firstName && { error: true, helperText: errors.firstName })}
                fullWidth
              />
              <TextField
                variant="outlined"
                name="middleName"
                size="small"
                label="Middle Name"
                value={values.middleName}
                onChange={handleInputChange}
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                variant="outlined"
                name="lastName"
                size="small"
                label={<RequiredLabel label="Last Name" />}
                value={values.lastName}
                onChange={handleInputChange}
                {...(errors.lastName && { error: true, helperText: errors.lastName })}
                fullWidth
              />
              <FormControl
                {...(errors.birthDate && { error: true })}
                fullWidth
              >
                <TextField
                  variant="outlined"
                  size="small"
                  type="date"
                  label={<RequiredLabel label="Birth Date" />}
                  name="birthDate"
                  defaultValue={new Date()}
                  value={values.birthDate}
                  onChange={handleInputChange}
                  fullWidth
                />
                {errors.birthDate && <FormHelperText>{errors.birthDate}</FormHelperText>}
              </FormControl>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <FormLabel sx={{ minWidth: '80px' }}>Gender</FormLabel>
              <FormControl component="fieldset" fullWidth {...(errors.gender && { error: true })}>
                <RadioGroup
                  row
                  name="gender"
                  value={values.gender}
                  onChange={handleInputChange}
                >
                  <FormControlLabel value="F" control={<Radio />} label="Female" />
                  <FormControlLabel value="M" control={<Radio />} label="Male" />
                  <FormControlLabel value="O" control={<Radio />} label="Other" />
                </RadioGroup>
                {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
              </FormControl>
            </Stack>

            <FormControl
              variant="outlined"
              {...(errors.maritalStatus && { error: true })}
              fullWidth
            >
              <InputLabel><RequiredLabel label="Marital Status" /></InputLabel>
              <Select
                label={<RequiredLabel label="Marital Status" />}
                name="maritalStatus"
                size="small"
                value={values.maritalStatus}
                onChange={handleInputChange}
              >
                <MenuItem value="">None</MenuItem>
                {
                  maritalStatusOptions.map(
                    item => (<MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)
                  )
                }
              </Select>
              {errors.maritalStatus && <FormHelperText>{errors.maritalStatus}</FormHelperText>}
            </FormControl>

            <TextField
              variant="outlined"
              name="parentsName"
              size="small"
              label="Father's/Husband's Name"
              value={values.parentsName}
              onChange={handleInputChange}
              {...(errors.parentsName && { error: true, helperText: errors.parentsName })}
              fullWidth
            />

            <Stack direction="row" alignItems="center" spacing={2}>
              <FormLabel sx={{ minWidth: '80px' }}>Relationship with Member</FormLabel>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  row
                  name="relation"
                  value={values.relation}
                  onChange={handleInputChange}
                >
                  <FormControlLabel value="F" control={<Radio />} label="Father" />
                  <FormControlLabel value="H" control={<Radio />} label="Husband" />
                </RadioGroup>
              </FormControl>
            </Stack>

            <TextField
              variant="outlined"
              name="address"
              label={<RequiredLabel label="Address" />}
              multiline
              maxRows={4}
              value={values.address}
              onChange={handleInputChange}
              {...(errors.address && { error: true, helperText: errors.address })}
              fullWidth
            />

            <TextField
              variant="outlined"
              name="pinCode"
              size="small"
              label={<RequiredLabel label="Pin Code" />}
              type='text'
              value={values.pinCode}
              onChange={handleInputChange}
              {...(errors.pinCode && { error: true, helperText: errors.pinCode })}
              fullWidth
            />

            <FormControl
              variant="outlined"
              {...(errors.roleId && { error: true })}
              fullWidth
            >
              <InputLabel><RequiredLabel label="Role" /></InputLabel>
              <Select
                label={<RequiredLabel label="Role" />}
                name="roleId"
                size="small"
                value={values.roleId}
                onChange={handleInputChange}
              >
                <MenuItem value="">None</MenuItem>
                {roles.map(role => (
                  <MenuItem
                    key={role.role_id}
                    value={role.role_id}
                  >
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.roleId && <FormHelperText>{errors.roleId}</FormHelperText>}
            </FormControl>

            <Stack direction="row" spacing={2}>
              {!hideStatutory && (
                <>
                  <FormControl
                    variant="outlined"
                    {...(errors.esicExempted && { error: true })}
                    fullWidth
                  >
                    <InputLabel><RequiredLabel label="ESIC Exempted"></RequiredLabel></InputLabel>
                    <Select
                      label="ESIC Exempted"
                      size="small"
                      name="esicExempted"
                      value={values.esicExempted}
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">None</MenuItem>
                      {esicExemptedOptions.map(
                        item => (<MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)
                      )}
                    </Select>
                    {errors.esicExempted && <FormHelperText>{errors.esicExempted}</FormHelperText>}
                  </FormControl>

                  {values.esicExempted === "0" && (
                    <TextField
                      variant="outlined"
                      name="esicNumber"
                      size="small"
                      label="ESIC Number"
                      value={values.esicNumber}
                      onChange={handleInputChange}
                      {...(errors.esicNumber && { error: true, helperText: errors.esicNumber })}
                      fullWidth
                    />
                  )}
                </>
              )}
            </Stack>

            <TextField
              variant="outlined"
              label={<RequiredLabel label="Mobile" />}
              name="mobile"
              type='text'
              size="small"
              value={values.mobile}
              onChange={handleInputChange}
              {...(errors.mobile && { error: true, helperText: errors.mobile })}
              fullWidth
            />

            <TextField
              variant="outlined"
              name="email"
              label={<RequiredLabel label="Email" />}
              type='email'
              size="small"
              value={values.email}
              onChange={handleInputChange}
              {...(errors.email && { error: true, helperText: errors.email })}
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <TextField
                variant="outlined"
                label={<RequiredLabel label={hideStatutory ? 'Intern/Apprentice Code' : 'Employee Code'} />}
                name="employeeCode"
                size="small"
                value={values.employeeCode}
                onChange={handleInputChange}
                {...(errors.employeeCode && { error: true, helperText: errors.employeeCode })}
                inputProps={hideStatutory ? { inputMode: 'text' } : undefined}
                fullWidth
              />
              <FormControl
                {...(errors.dateOfJoining && { error: true })}
                fullWidth
              >
                <TextField
                  variant="outlined"
                  type="date"
                  label={<RequiredLabel label="Date of Joining" />}
                  name="dateOfJoining"
                  size="small"
                  InputProps={{
                    inputProps: {
                      max: new Date().toISOString().split('T')[0]
                    }
                  }}
                  value={values.dateOfJoining}
                  onChange={handleInputChange}
                  fullWidth
                />
                {errors.dateOfJoining && <FormHelperText>{errors.dateOfJoining}</FormHelperText>}
              </FormControl>
            </Stack>

            {props.fetch_id !== 9999 && (values.originalDateOfJoining || values.originalDateOfTermination) && (
              <Stack direction="row" spacing={2}>
                {values.originalDateOfJoining && (
                  <TextField
                    variant="outlined"
                    type="date"
                    label="Original Date of Joining"
                    name="originalDateOfJoining"
                    size="small"
                    value={values.originalDateOfJoining}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                )}
                {values.originalDateOfTermination && (
                  <TextField
                    variant="outlined"
                    type="date"
                    label="Original Termination Date"
                    name="originalDateOfTermination"
                    size="small"
                    value={values.originalDateOfTermination}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                )}
              </Stack>
            )}

            <TextField
              variant="outlined"
              label={<RequiredLabel label={hideStatutory ? "Stipend Account Number" : "Salary Account Number"} />}
              name="salAccNo"
              type="text"
              inputMode="numeric"
              size="small"
              value={values.salAccNo}
              onChange={handleInputChange}
              {...(errors.salAccNo && { error: true, helperText: errors.salAccNo })}
              fullWidth
            />

            <TextField
              variant="outlined"
              label="Bank Name"
              name="bank_name"
              size="small"
              value={values.bank_name}
              onChange={handleInputChange}
              fullWidth
            />

            <TextField
              variant="outlined"
              label="IFSC Code"
              name="ifsc_code"
              size="small"
              value={values.ifsc_code}
              onChange={handleInputChange}
              {...(errors.ifsc_code && { error: true, helperText: errors.ifsc_code })}
              fullWidth
            />

            <TextField
              variant="outlined"
              label={<RequiredLabel label="PAN Number" />}
              name="pan"
              size="small"
              value={values.pan}
              onChange={handleInputChange}
              {...(errors.pan && { error: true, helperText: errors.pan })}
              fullWidth
            />

            {!hideStatutory ? (
              <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 'auto' }}>
                  <FormControlLabel
                  control={<Checkbox
                    name="isPfApplicable"
                    color="primary"
                    checked={values.isPfApplicable}
                    onChange={e => {
                      const checked = e.target.checked;
                      if (!checked) {
                        setValues(prev => ({
                          ...prev,
                          isPfApplicable: false,
                          isVpfApplicable: false,
                          vpfDeductionType: 'P',
                          vpfPercent: 0.0,
                          vpfFixedAmount: 0.0,
                        }));
                      } else {
                        handleInputChange(convertToDefEventPara("isPfApplicable", true));
                      }
                    }}
                  />}
                  label="PF Applicable?"
                />
                </FormControl>

                <TextField
                  variant="outlined"
                  label="Name as per ECR Repository"
                  size="small"
                  name="full_name"
                  value={(values.full_name || '').toUpperCase()}
                  onChange={handleInputChange}
                  InputProps={{
                    readOnly: props.fetch_id === 9999,
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
            ) : (
              <TextField
                variant="outlined"
                label="Name as per ECR Repository"
                size="small"
                name="full_name"
                value={(values.full_name || '').toUpperCase()}
                onChange={handleInputChange}
                InputProps={{
                  readOnly: props.fetch_id === 9999,
                }}
                fullWidth
              />
            )}

            {!hideStatutory && values.isPfApplicable && <>
              <Stack direction="row" spacing={2}>
                <TextField
                  variant="outlined"
                  label={<RequiredLabel label="PF Number" />}
                  name="pfNo"
                  size="small"
                  value={values.pfNo}
                  onChange={handleInputChange}
                  {...(errors.pfNo && { error: true, helperText: errors.pfNo })}
                  fullWidth
                />

                <TextField
                  variant="outlined"
                  label={<RequiredLabel label="PF Limit" />}
                  size="small"
                  name="pfLimit"
                  value={values.pfLimit}
                  onChange={handleInputChange}
                  {...(errors.pfLimit && { error: true, helperText: errors.pfLimit })}
                  fullWidth
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  variant="outlined"
                  label={<RequiredLabel label="UAN No." />}
                  size="small"
                  name="uanNo"
                  value={values.uanNo}
                  onChange={handleInputChange}
                  {...(errors.uanNo && { error: true, helperText: errors.uanNo })}
                  fullWidth
                />
                <Box sx={{ flex: 1 }} />
              </Stack>

              <FormControl fullWidth>
                <FormControlLabel
                  control={<Checkbox
                    name="isVpfApplicable"
                    color="primary"
                    checked={values.isVpfApplicable}
                    onChange={e => {
                      const checked = e.target.checked;
                      setValues(prev => ({
                        ...prev,
                        isVpfApplicable: checked,
                        ...(checked ? {} : { vpfPercent: 0.0, vpfFixedAmount: 0.0, vpfDeductionType: 'P' }),
                      }));
                    }}
                  />}
                  label="VPF Applicable?"
                />
              </FormControl>

              {values.isVpfApplicable && <>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">VPF Deduction (on Gross)</FormLabel>
                  <RadioGroup
                    row
                    name="vpfDeductionType"
                    value={values.vpfDeductionType}
                    onChange={e => {
                      const type = e.target.value;
                      setValues(prev => ({
                        ...prev,
                        vpfDeductionType: type,
                        ...(type === 'P'
                          ? { vpfFixedAmount: 0.0 }
                          : { vpfPercent: 0.0 }),
                      }));
                    }}
                  >
                    <FormControlLabel value="P" control={<Radio />} label="Deduction by Percentage" />
                    <FormControlLabel value="A" control={<Radio />} label="Deduction by Amount" />
                  </RadioGroup>
                </FormControl>
                <Stack direction="row" spacing={2}>
                  {values.vpfDeductionType === 'P' ? (
                    <TextField
                      variant="outlined"
                      label={<RequiredLabel label="VPF % of Gross" />}
                      size="small"
                      name="vpfPercent"
                      value={values.vpfPercent}
                      onChange={handleInputChange}
                      {...(errors.vpfPercent && { error: true, helperText: errors.vpfPercent })}
                      fullWidth
                    />
                  ) : (
                    <TextField
                      variant="outlined"
                      label={<RequiredLabel label="VPF Amount" />}
                      size="small"
                      name="vpfFixedAmount"
                      value={values.vpfFixedAmount}
                      onChange={handleInputChange}
                      {...(errors.vpfFixedAmount && { error: true, helperText: errors.vpfFixedAmount })}
                      fullWidth
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                </Stack>
              </>}
            </>}

            {!hideStatutory && WF_Applicable_for_Company === 1 &&
              <FormControl fullWidth>
                <FormControlLabel
                  control={<Checkbox
                    name="isWfApplicable"
                    color="primary"
                    checked={values.isWfApplicable}
                    onChange={e => handleInputChange(convertToDefEventPara("isWfApplicable", e.target.checked))}
                  />}
                  label="WF Applicable?"
                />
              </FormControl>}

            {!hideStatutory && values.isWfApplicable && <>
              <TextField
                variant="outlined"
                label={<RequiredLabel label="WF Number" />}
                name="wfNumber"
                size="small"
                value={values.wfNumber}
                onChange={handleInputChange}
                {...(errors.wfNumber && { error: true, helperText: errors.wfNumber })}
                fullWidth
              />
            </>}

            {/* Only show "Is Employee Leaving?" section when editing (not adding new) */}
            {props.fetch_id !== 9999 && (
              <>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isLeaving"
                        color="primary"
                        checked={values.isLeaving}
                        onChange={e => handleInputChange(convertToDefEventPara("isLeaving", e.target.checked))}
                      />
                    }
                    label={hideStatutory ? "Is Intern/Apprentice Leaving?" : "Is Employee Leaving?"}
                  />
                </FormControl>

                {values.isLeaving && (
                  <>
                    {/* 1. Date of Resignation & Last Working Day */}
                    <Stack direction="row" spacing={2}>
                      <TextField
                        variant="outlined"
                        type="date"
                        label="Date of Resignation"
                        size="small"
                        name="dateOfResignation"
                        value={values.dateOfResignation ?? ''}
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        variant="outlined"
                        type="date"
                        label="Last Working Day"
                        size="small"
                        name="dateOfLeaving"
                        value={values.dateOfLeaving ?? ''}
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Stack>

                    {/* 2. Consider Notice Period checkbox – only for employees */}
                    {!hideStatutory && (
                      <FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="considerNoticePeriod"
                              color="primary"
                              checked={values.considerNoticePeriod}
                              onChange={e => handleInputChange(convertToDefEventPara("considerNoticePeriod", e.target.checked))}
                            />
                          }
                          label="Consider Notice Period"
                        />
                      </FormControl>
                    )}

                    {!hideStatutory && values.considerNoticePeriod && (
                      <>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            variant="outlined"
                            label="Official Notice Period"
                            size="small"
                            name="officialNoticePeriod"
                            value={values.officialNoticePeriod}
                            onChange={handleInputChange}
                            fullWidth
                          />
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel>Notice Given By Whom</InputLabel>
                            <Select
                              label="Notice Given By Whom"
                              name="noticeGivenByWhom"
                              size="small"
                              value={values.noticeGivenByWhom}
                              onChange={handleInputChange}
                            >
                              <MenuItem value="">None</MenuItem>
                              <MenuItem value="1">Company</MenuItem>
                              <MenuItem value="2">Employee</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                          <FormControl>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="shortNoticePeriod"
                                  color="primary"
                                  checked={values.shortNoticePeriod}
                                  onChange={e => handleInputChange(convertToDefEventPara("shortNoticePeriod", e.target.checked))}
                                />
                              }
                              label="Short Notice Period"
                            />
                          </FormControl>
                          {values.noticeGivenByWhom === '2' && (
                            <FormControl>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="earningOrRecovery"
                                    color="primary"
                                    checked={values.earningOrRecovery}
                                    onChange={e => handleInputChange(convertToDefEventPara("earningOrRecovery", e.target.checked))}
                                  />
                                }
                                label="Is Recovery?"
                              />
                            </FormControl>
                          )}
                          {values.noticeGivenByWhom === '1' && (
                            <FormControl>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="isNoticePayout"
                                    color="primary"
                                    checked={values.isNoticePayout}
                                    onChange={e => handleInputChange(convertToDefEventPara("isNoticePayout", e.target.checked))}
                                  />
                                }
                                label="Is Payout?"
                              />
                            </FormControl>
                          )}
                        </Stack>
                      </>
                    )}

                    {/* 3. Reason of Leaving */}
                    <FormControl
                      variant="outlined"
                      {...(errors.leavingReason && { error: true })}
                      fullWidth
                    >
                      <InputLabel>Reason of Leaving</InputLabel>
                      <Select
                        label="Reason of Leaving"
                        name="leavingReason"
                        size="small"
                        value={values.leavingReason}
                        onChange={handleInputChange}
                      >
                        <MenuItem value="">None</MenuItem>
                        {
                          [
                            ...(hideStatutory
                              ? leavingReasonOptions.filter(r => String(r.title).toUpperCase() !== 'RETIREMENT')
                              : leavingReasonOptions),
                            ...(hideStatutory
                              ? [{ id: 999, title: 'INTERNSHIP / APPRENTICESHIP FINISHED' }]
                              : [])
                          ].map(item => {
                            const title = hideStatutory
                              ? String(item.title).replace(/employee/gi, 'Intern/Apprentice')
                              : item.title;
                            return (
                              <MenuItem key={item.id} value={item.id}>
                                {title}
                              </MenuItem>
                            );
                          })
                        }
                      </Select>
                      {errors.leavingReason && <FormHelperText>{errors.leavingReason}</FormHelperText>}
                    </FormControl>

                    {/* 4. Is Regretted Exit */}
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend"><RequiredLabel label="Is Regretted Exit?" /></FormLabel>
                      <RadioGroup row name="isRegrettedExit" value={values.isRegrettedExit ? '1' : '0'} onChange={e => handleInputChange(convertToDefEventPara("isRegrettedExit", e.target.value === '1'))}>
                        <FormControlLabel value="1" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="0" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </>
                )}
              </>
            )}

            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button variant="contained" color="error" onClick={resetForm} disabled={buttonDisabled}>Reset</Button>
              </Grid>
              <Grid item>
                <Button variant="contained" type="submit" onSubmit={handleSubmit} disabled={buttonDisabled}>Submit</Button>
              </Grid>
            </Grid>
          </Stack>
        </form>

        <Stack alignItems="flex-end" sx={{ margin: "20px" }}>
          <Button variant="outlined" onClick={() => setOpenPopup(true)} endIcon={<ArrowForwardIos />} >Next</Button>
        </Stack>

        <Popup
          title="Company Bank Accounts Form"
          openPopup={openPopup}
          setOpenPopup={setOpenPopup}
        >
          <ViewEmpSalComponents employeeID={employee_id} fetch_id={props.fetch_id} />
        </Popup>
      </Paper>
      <Notification
        notify={notify}
        setNotify={setNotify}
      />
    </Box>
  );
}

export default AddEmployeeForm;